import React, { useState, useEffect } from 'react'
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react'

export default function LightboxGallery({ images, title, description }) {
  const [lightboxIndex, setLightboxIndex] = useState(null)

  const openLightbox = (index) => {
    setLightboxIndex(index)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxIndex(null)
    document.body.style.overflow = 'unset'
  }

  const nextImage = (e) => {
    e?.stopPropagation()
    setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const prevImage = (e) => {
    e?.stopPropagation()
    setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') nextImage()
      if (e.key === 'ArrowLeft') prevImage()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxIndex])

  if (!images || images.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Ingen billeder tilgængelige i dette galleri.
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Header */}
      {(title || description) && (
        <div className="mb-8">
          {title && <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>}
          {description && <p className="text-gray-600 text-sm">{description}</p>}
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((imgUrl, index) => (
          <div
            key={`${imgUrl}-${index}`}
            onClick={() => openLightbox(index)}
            className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in-up"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <img
              src={imgUrl.includes('assets.auxodev.dk') && !imgUrl.includes('/cdn-cgi/image/') ? imgUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/') : imgUrl}
              alt={`${title || 'Galleri'} - Billede ${index + 1}`}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 select-none animate-in fade-in duration-200"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-50 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            aria-label="Luk"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={prevImage}
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            aria-label="Forrige"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            aria-label="Næste"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Image Container */}
          <div className="relative max-w-5xl max-h-[80vh] flex flex-col items-center justify-center">
            <img
              src={images[lightboxIndex].includes('assets.auxodev.dk') && !images[lightboxIndex].includes('/cdn-cgi/image/') ? images[lightboxIndex].replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/') : images[lightboxIndex]}
              alt="Forstørret visning"
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Caption / Counter */}
            <div className="text-white/80 text-sm font-semibold mt-4">
              Billede {lightboxIndex + 1} af {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
